<template>
  <v-app-bar
    app
    flat
    clipped-right
    height="56"
    class="industrial-app-bar"
    :dark="!isLightAppBar"
    :light="isLightAppBar"
    :style="appBarStyle"
  >
    <div class="app-bar__left">
      <v-btn
        icon
        class="app-bar__menu-btn"
        aria-label="เปิดเมนู"
        @click.stop="$emit('drawerChange')"
      >
        <v-icon :color="contrast.icon">mdi-menu</v-icon>
      </v-btn>

      <router-link to="/" class="app-bar__brand text-decoration-none">
        <v-img
          v-if="logoSrc"
          contain
          max-height="32"
          max-width="160"
          :src="logoSrc"
          class="app-bar__logo"
          @error="onLogoError"
        />
        <span v-else class="app-bar__brand-text">{{ appName }}</span>
      </router-link>
    </div>

    <v-spacer />

    <div class="app-bar__right">
      <div v-if="refreshTime" class="app-bar__sync d-none d-sm-flex">
        <v-icon x-small class="app-bar__sync-icon">mdi-sync</v-icon>
        <span class="app-bar__sync-label">SYNC</span>
        <span class="app-bar__sync-time">{{ refreshTime }}</span>
      </div>

      <v-btn
        icon
        class="app-bar__action-btn"
        aria-label="การแจ้งเตือน"
        @click.stop="$emit('alarmDrawerChange')"
      >
        <v-badge
          :content="alertLatest.length"
          :value="alertLatest.length > 0"
          color="error"
          overlap
          offset-x="8"
          offset-y="8"
        >
          <v-icon :color="contrast.icon">mdi-bell-outline</v-icon>
        </v-badge>
      </v-btn>

      <div class="app-bar__user" v-if="!$vuetify.breakpoint.mobile">
        <div class="app-bar__user-info">
          <div class="app-bar__user-role">ผู้ดูแลอาคาร</div>
          <div class="app-bar__user-name">{{ user.name }}</div>
          <div class="app-bar__user-tenant">{{ tenant.TenantName }}</div>
        </div>
      </div>

      <UserMenu @userProfile="profileDialog = true" />
    </div>

    <v-snackbar v-model="network" :timeout="-1" bottom app color="error">
      <v-icon left small>mdi-wifi-off</v-icon>
      ขาดการเชื่อมต่ออินเทอร์เน็ต
      <template v-slot:action="{ attrs }">
        <v-btn text v-bind="attrs" @click="network = false">ปิด</v-btn>
      </template>
    </v-snackbar>

    <v-dialog v-model="modal" max-width="400" style="z-index: 99999;">
      <v-card class="industrial-panel" rounded="sm">
        <v-card-title class="font-weight-bold">
          <v-icon left color="error">mdi-alert-circle</v-icon>
          {{ title }}
        </v-card-title>
        <v-card-text class="error--text">
          {{ message }}
        </v-card-text>
        <v-card-actions class="px-4 pb-4">
          <v-btn color="primary" block depressed @click="postAcknowledge" :loading="posting">
            รับทราบ
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app-bar>
</template>

<script>
import UserMenu from '@/components/layout/UserMenu.vue'
import { mapState } from 'vuex'
import { resolveBranding, isLightBackground } from '@/utils/tenantBranding'
import { APP_NAME } from '@/config/appBrand'
import { initSignalR } from '@/plugins/signalr'
import { EventBus } from '@/plugins/event-bus'
import { ringtone } from '@/plugins/ringtone'

export default {
  components: {
    UserMenu,
  },
  model: {
    prop: 'drawer',
    event: 'change'
  },
  props: {
    drawer: {
      type: Boolean
    },
  },
  computed: {
    ...mapState('User', ['user', 'tenant', 'roles', 'tenantBranding']),
    ...mapState('Alert', ['alertLatest']),
    branding() {
      return resolveBranding(this.tenantBranding)
    },
    appBarColor() {
      return this.branding.appBarColor
    },
    contrast() {
      return this.branding.contrast
    },
    isLightAppBar() {
      return isLightBackground(this.appBarColor)
    },
    appBarStyle() {
      return {
        '--bar-color': this.appBarColor,
        '--bar-text': this.contrast.text,
        '--bar-text-muted': this.contrast.textMuted,
        '--bar-text-subtle': this.contrast.textSubtle,
        '--bar-border': this.contrast.border,
        '--bar-btn-bg': this.contrast.buttonBg,
        '--bar-btn-bg-hover': this.contrast.buttonBgHover,
        '--bar-sync-bg': this.contrast.syncBg,
        '--bar-accent': this.contrast.accent,
      }
    },
    logoSrc() {
      if (this.logoError) return null
      return this.branding.logo
    },
    building: {
      get() {
        return this.$store.state.User.building
      },
      set(newValue) {
        this.$store.dispatch('User/SET_BUILDING', newValue)
      }
    },
  },
  watch: {
    tenantBranding() {
      this.logoError = false
    },
  },
  mounted() {
    initSignalR()
    window.addEventListener('offline', this.isOffLine)
    window.addEventListener('online', this.isOnLine)
    ringtone.muted = true
    EventBus.$on('alarm', this.playAlarm)
    this.autoRefresh()
    this.refreshTime = this.$dayjs().format('DD MMM YYYY HH:mm')
  },
  async destroyed() {
    EventBus.$off('alarm', this.playAlarm)
  },
  methods: {
    onLogoError() {
      this.logoError = true
    },
    isOnLine() {
      this.network = false
    },
    isOffLine() {
      this.network = true
    },
    playAlarm(device) {
      this.refresh()
      this.modal = true
      this.device = device
      this.title = `แจ้งเตือน ${device.assetName}`
      this.message = `${device.message}`
      this.play()
    },
    stopAlarm() {
      ringtone.pause()
      this.modal = false
    },
    postAcknowledge() {
      this.posting = true
      const data = {
        tenantId: this.device.tenantId,
        buildingId: this.device.buildingId,
        message: this.message
      }
      this.$store.dispatch('Acknowledge/POST_ACKNOWLEDGE', data).then(() => {
        this.posting = false
        ringtone.pause()
        this.modal = false
      }).catch((err) => {
        this.posting = false
        ringtone.pause()
        this.modal = false
        console.log(err)
      })
    },
    refresh() {
      this.$store.dispatch('Asset/GET_ALL_ASSET', this.building).then(() => {
        this.refreshTime = this.$dayjs().format('DD MMM YYYY HH:mm')
      }).catch((err) => {
        console.log(err)
      })
    },
    autoRefresh() {
      this.interval = setInterval(() => { this.refresh() }, 30000)
    },
    play() {
      ringtone.volume = 1
      ringtone.muted = false
      ringtone.loop = true
      const resp = ringtone.play()
      if (resp !== undefined) {
        resp.catch(error => {
          console.log(error)
        })
      }
    }
  },
  destroyed() {
    if (this.interval) {
      clearInterval(this.interval)
    }
    window.removeEventListener('offline', this.isOffLine)
    window.removeEventListener('online', this.isOnLine)
  },
  data() {
    return {
      appName: APP_NAME,
      logoError: false,
      message: '',
      modal: false,
      title: '',
      interval: null,
      device: null,
      posting: false,
      network: false,
      refreshTime: null,
    }
  },
}
</script>

<style scoped>
.industrial-app-bar {
  background-color: var(--bar-color) !important;
  box-shadow: none !important;
  border-bottom: 2px solid var(--bar-accent, #D97706);
  color: var(--bar-text, #fff);
}

.industrial-app-bar >>> .v-toolbar__content {
  color: var(--bar-text, #fff);
  padding: 0 12px 0 8px;
}

.app-bar__left {
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.app-bar__menu-btn {
  background: var(--bar-btn-bg) !important;
  border-radius: 4px !important;
  transition: background 0.2s;
}

.app-bar__menu-btn:hover {
  background: var(--bar-btn-bg-hover) !important;
}

.app-bar__brand {
  display: flex;
  align-items: center;
  flex-shrink: 0;
}

.app-bar__logo {
  display: block;
}

.app-bar__brand-text {
  font-size: 0.95rem;
  font-weight: 600;
  color: var(--bar-text, #fff);
  letter-spacing: 0.02em;
  white-space: nowrap;
}

.app-bar__right {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.app-bar__sync {
  align-items: center;
  gap: 6px;
  background: var(--bar-sync-bg);
  border: 1px solid var(--bar-border);
  border-radius: 4px;
  padding: 4px 10px;
  margin-right: 4px;
  line-height: 1;
}

.app-bar__sync-icon {
  color: var(--bar-accent, #D97706) !important;
}

.app-bar__sync-label {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 0.6rem;
  color: var(--bar-text-subtle);
  letter-spacing: 0.08em;
  font-weight: 600;
}

.app-bar__sync-time {
  font-family: 'IBM Plex Mono', monospace;
  font-size: 0.72rem;
  color: var(--bar-text, #fff);
  font-weight: 500;
  white-space: nowrap;
}

.app-bar__action-btn {
  background: var(--bar-btn-bg) !important;
  border-radius: 4px !important;
  transition: background 0.2s;
}

.app-bar__action-btn:hover {
  background: var(--bar-btn-bg-hover) !important;
}

.app-bar__user {
  display: flex;
  align-items: center;
  padding: 4px 4px 4px 12px;
  margin-left: 2px;
  border-left: 1px solid var(--bar-border);
}

.app-bar__user-info {
  text-align: right;
  line-height: 1.25;
  max-width: 180px;
}

.app-bar__user-role {
  font-size: 0.6rem;
  color: var(--bar-accent, #D97706);
  text-transform: uppercase;
  letter-spacing: 0.06em;
  font-weight: 600;
}

.app-bar__user-name {
  font-size: 0.82rem;
  font-weight: 600;
  color: var(--bar-text, #fff);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.app-bar__user-tenant {
  font-size: 0.68rem;
  color: var(--bar-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 1px;
}

.industrial-panel {
  border: 1px solid var(--border-subtle, #D1D5DB);
}

@media (max-width: 599px) {
  .industrial-app-bar >>> .v-toolbar__content {
    padding: 0 8px 0 4px;
  }
}
</style>
